import { CommonModule } from '@angular/common';
import { Component, inject, Optional, signal, Signal, OnInit, OnDestroy, computed } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ModalService } from '@app/_services/modal.service';
import { FeatureSetup } from '@app/_interfaces/feature.interface';
import { FeatureStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { JournalSetupFormComponent } from '../../journals/journal-setup-form/journal-setup-form.component';
import { MoneyTrackerSetupFormComponent } from '../../money-tracker/money-tracker-setup-form/money-tracker-setup-form.component';
import { CalendarConnectComponent } from '../../calendar-integration/calendar-connect/calendar-connect.component';
import { HabitSetupFormComponent } from '../../habits/habit-setup-form/habit-setup-form.component';
import { FocusSetupFormComponent } from '../../focus-tracker/focus-setup-form/focus-setup-form.component';
import { Router } from '@angular/router';
import { CacheService } from '@app/_services/cache.service';
import { SettingsService } from '@app/_services/settings.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { DependencyService } from '@app/_services/dependency.service';
import { log } from '@app/_services/logger';

@Component({
  selector: 'app-feature-list',
  imports: [
    CommonModule,
    SvgComponent
  ],
  templateUrl: './feature-list.component.html',
  styleUrl: './feature-list.component.scss'
})

export class FeatureListComponent implements OnInit, OnDestroy {

  readonly featureStore = inject(FeatureStore);
  unSubscribe = new Subject<void>();
  screenEnterTime: Signal<Date> = signal(new Date());

  constructor(
    @Optional() public dialogRef: MatDialogRef<FeatureListComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<FeatureListComponent> | null,
    public dialog: MatDialog,
    private modalService: ModalService,
    public cc: CacheService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private router: Router,
    public ds: DependencyService,
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
  }

  subscriptionExpDate: Signal<Date | null> = computed(() => {
    return this.ss.user()?.subscriptionInfo?.subscriptionExpDate || null;
  });

  addFeature(feature: FeatureSetup) {
    switch (feature.entityType) {
      case 'boolean':
      case 'timer':
      case 'numeric':
      case 'single':
      case 'multiple':

        const data = {
          mode: 'new',
          value: null,
          habitType: feature.entityType,
        };
        if (this.cc.isNarrowScreen()) {
          const setupDialog = this.modalService.openMainContent(
            HabitSetupFormComponent,
            data,
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupDialog = this.modalService.openLevel1(
            HabitSetupFormComponent,
            data,
            { disableClose: true, autoFocus: false }
          );
        }
        break;
      case 'journal':

        const journalData = {
          mode: 'new',
          value: null,
        }

        if (this.cc.isNarrowScreen()) {
          const setupDialog = this.modalService.openMainContent(
            JournalSetupFormComponent,
            journalData,
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupDialog = this.modalService.openLevel1(
            JournalSetupFormComponent,
            journalData,
            { disableClose: true, autoFocus: false }
          );
        }
        break;
      case 'moneyTracker':


        const moneyData = {
          mode: 'new',
          value: null,
        }

        if (this.cc.isNarrowScreen()) {
          const setupDialog = this.modalService.openMainContent(
            MoneyTrackerSetupFormComponent,
            moneyData,
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupDialog = this.modalService.openLevel1(
            MoneyTrackerSetupFormComponent,
            moneyData,
            { disableClose: true, autoFocus: false }
          );
        }
        break;
      case 'focus':

        const focusData = {
          mode: 'new',
          value: null,
        }

        if (this.cc.isNarrowScreen()) {
          const setupDialog = this.modalService.openMainContent(
            FocusSetupFormComponent,
            focusData,
            { disableClose: true, autoFocus: false }
          );
        } else {
          const setupDialog = this.modalService.openLevel1(
            FocusSetupFormComponent,
            focusData,
            { disableClose: true, autoFocus: false }
          );
        }
        break;
      case 'google':
      case 'microsoft':

        if (!this.ss.isPremiumUser()) {
          this.ds.featureUnavailable(!this.subscriptionExpDate());
          return;
        }

        const setupCalendarDialog = this.dialog.open(CalendarConnectComponent, {
          width: '100%',
          maxWidth: '500px',
          height: 'auto',
          disableClose: true,
          data: {
            type: feature.entityType
          },
        });
        break;

      default:
        log.warn('FeatureListComponent.addFeature', `Unknown entityType: ${feature.entityType}`, { entityType: feature.entityType });
        break;
    }
    this.closeDialog();
  }

  async closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('addFeaturesSheet', previousScreen);
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('addFeaturesSheet', this.screenEnterTime());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
